/**
 * Session continuity for the gateway drawer: the thread that is still there
 * after F5, a browser restart, or a jump into the editor.
 *
 * This is not a replacement for assistantHistory.js. The two write different
 * things for different reasons and both keep running on every turn:
 *
 *   assistantHistory.js  -> ai_assistant_conversations. An append-only
 *                           investigation log nobody owns, replaced wholesale
 *                           on each save. It is the ONLY place a *rejected*
 *                           mutation is recorded, so it stays exactly as it is.
 *
 *   this module           -> agent_conversations + agent_messages. An owned,
 *                           resumable thread appended to one message at a
 *                           time, which is what makes reading it back on mount
 *                           possible at all.
 *
 * Three rules shape it:
 *
 *   1. Persistence NEVER breaks the chat. Every write is awaited but its
 *      failure is swallowed into `lastError`; a backend that is down costs the
 *      operator continuity, not the answer they are waiting for.
 *
 *   2. Appends are serialized. Messages carry an explicit seq, so two writes
 *      racing would either collide with each other or land out of order. One
 *      promise chain per store removes the question.
 *
 *   3. A 409 is expected, not exceptional. Two tabs on one thread both compute
 *      the same next position; the loser is told the free one and resends the
 *      SAME message there. That retry is the reason messages are rows rather
 *      than a blob — a blob upsert would have silently dropped the other tab's
 *      turn instead.
 */

// One key per surface, so the editor's Chat tab and this drawer resume their
// own threads rather than fighting over one slot. sessionStorage, not local:
// a second window is a second train of thought, and F5 is what this is for.
export const ACTIVE_CONVERSATION_KEY_PREFIX = "cs.agent.active.";

export const GATEWAY_SURFACE = "gateway";

// The id grammar the server validates: [A-Za-z0-9_-]{1,64}.
const ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

// How many times a losing append retries at the position the 409 named. More
// than a couple means something other than a second tab is writing, and
// hammering the endpoint would not fix it.
const DEFAULT_MAX_RETRIES = 5;

export function activeConversationKey(surface = GATEWAY_SURFACE) {
  return `${ACTIVE_CONVERSATION_KEY_PREFIX}${surface}`;
}

export function isValidConversationId(id) {
  return typeof id === "string" && ID_PATTERN.test(id);
}

// Client-generated so the thread has an identity before the first request
// lands; the `gw` prefix keeps it apart from ids imported from the editor.
export function newAgentConversationId() {
  return `gw${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
}

function storageOf(storage) {
  if (storage) return storage;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Which conversation to restore on mount.
 *
 * `?chat=<id>` wins over the stored id: it is how a deep link from the editor
 * (or a link somebody pasted to a colleague) carries the thread across SPAs,
 * and an explicit link must beat whatever this tab happened to be doing.
 */
export function readActiveConversationId({
  surface = GATEWAY_SURFACE,
  search = typeof window !== "undefined" ? window.location.search : "",
  storage,
} = {}) {
  try {
    const fromUrl = new URLSearchParams(search || "").get("chat");
    if (isValidConversationId(fromUrl)) return fromUrl;
  } catch {}
  try {
    const stored = storageOf(storage)?.getItem(activeConversationKey(surface));
    if (isValidConversationId(stored)) return stored;
  } catch {}
  return "";
}

export function rememberActiveConversationId(id, { surface = GATEWAY_SURFACE, storage } = {}) {
  if (!isValidConversationId(id)) return;
  try {
    storageOf(storage)?.setItem(activeConversationKey(surface), id);
  } catch {}
}

export function forgetActiveConversationId({ surface = GATEWAY_SURFACE, storage } = {}) {
  try {
    storageOf(storage)?.removeItem(activeConversationKey(surface));
  } catch {}
}

/**
 * Rehydrate stored messages into the drawer's own `conversation` shape.
 *
 * Attachment bytes were never persisted (they arrive as multi-megabyte base64
 * data URLs), so a restored image is a named chip, not a broken <img>. A tool
 * message has no place in the rendered transcript — the drawer shows prose and
 * approval cards — so it is skipped here while staying in the record.
 */
export function messagesToConversation(messages) {
  return (Array.isArray(messages) ? messages : [])
    .filter((message) => message?.role === "user" || message?.role === "assistant")
    .map((message) => {
      const content = message.content && typeof message.content === "object" ? message.content : {};
      const entry = {
        role: message.role,
        content: typeof content.text === "string" ? content.text : "",
      };
      const names = Array.isArray(content.attachmentNames)
        ? content.attachmentNames.filter((name) => typeof name === "string" && name)
        : [];
      if (names.length > 0) entry.attachmentNames = names;
      return entry;
    })
    // A turn that was opened but never answered leaves an empty assistant
    // message behind. Restoring it would put a blank bubble under the last
    // question for no reason.
    .filter((entry) => entry.content !== "" || (entry.attachmentNames?.length ?? 0) > 0);
}

/**
 * @param {Object} opts
 * @param {(payload: Object) => Promise<any>} opts.createConversation
 * @param {(id: string, payload: Object) => Promise<any>} opts.appendMessage
 * @param {(id: string) => Promise<any>} opts.getConversation
 * @param {string} [opts.surface]
 * @param {string} [opts.userName]
 * @param {() => string} [opts.originRoute]  read at create time
 * @param {Storage} [opts.storage]           injectable for tests
 * @param {number} [opts.maxRetries]
 */
export function createAgentConversationStore({
  createConversation,
  appendMessage,
  getConversation,
  surface = GATEWAY_SURFACE,
  userName = "",
  originRoute = () => (typeof window !== "undefined" ? window.location.pathname : ""),
  storage,
  maxRetries = DEFAULT_MAX_RETRIES,
} = {}) {
  let id = newAgentConversationId();
  let nextSeq = 0;
  let provider = "";
  let model = "";
  let lastError = null;
  // The conversation row is created lazily, on the first message. "New chat"
  // must not put an empty thread in everyone's list for a question the
  // operator decided not to ask.
  let ensured = null;
  // One promise chain: appends carry an explicit seq, so letting two run at
  // once would make this tab race itself for no benefit.
  let queue = Promise.resolve();

  const ensureConversation = () => {
    if (ensured) return ensured;
    ensured = Promise.resolve(
      createConversation({
        id,
        surface,
        userName,
        originRoute: originRoute() || "",
        ...(provider ? { provider } : {}),
        ...(model ? { model } : {}),
      })
    ).then((row) => {
      rememberActiveConversationId(id, { surface, storage });
      return row;
    });
    return ensured;
  };

  // The 409 loop. The server answers a collision with the free position, so a
  // retry is one extra request rather than a re-read of the whole thread.
  const appendWithRetry = async (message) => {
    for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
      try {
        const result = await appendMessage(id, { ...message, seq: nextSeq });
        nextSeq = Number.isFinite(result?.nextSeq) ? result.nextSeq : nextSeq + 1;
        return result;
      } catch (err) {
        const suggested = err?.status === 409 ? err?.body?.nextSeq : undefined;
        if (!Number.isFinite(suggested)) throw err;
        // Someone else took this position. Move to the one they left free and
        // send the SAME message again — that is what keeps the turn.
        nextSeq = suggested;
      }
    }
    throw new Error("gave up appending after repeated position conflicts");
  };

  // Every write goes through here so a failure is recorded and dropped rather
  // than thrown into a chat turn.
  const enqueue = (work) => {
    queue = queue.then(async () => {
      try {
        await ensureConversation();
        await work();
        lastError = null;
      } catch (err) {
        lastError = err;
        // A create that failed must be retried by the next message, not
        // remembered as done.
        if (!(err?.status >= 400 && err?.status < 500)) ensured = null;
      }
    });
    return queue;
  };

  return {
    get conversationId() {
      return id;
    },
    get nextSeq() {
      return nextSeq;
    },
    get lastError() {
      return lastError;
    },
    // Exposed so a caller can await persistence in a test without reaching
    // into the queue.
    settled() {
      return queue;
    },

    setModel(nextProvider, nextModel) {
      provider = typeof nextProvider === "string" ? nextProvider : "";
      model = typeof nextModel === "string" ? nextModel : "";
    },

    /**
     * Load a stored thread and continue it. Returns the rendered messages, or
     * null when the id is gone (an archived thread somebody linked, a stale
     * sessionStorage entry after a database reset) — in which case the caller
     * simply starts fresh instead of showing an error for something the
     * operator did not do.
     */
    async resume(conversationId) {
      if (!isValidConversationId(conversationId)) return null;
      try {
        const stored = await getConversation(conversationId);
        if (!stored?.id) return null;
        id = stored.id;
        const messages = Array.isArray(stored.messages) ? stored.messages : [];
        nextSeq = messages.reduce(
          (highest, message) => Math.max(highest, Number(message?.seq ?? -1) + 1),
          0
        );
        // The row exists, so nothing needs creating; resolving `ensured` is
        // what stops the next append from POSTing a duplicate create.
        ensured = Promise.resolve(stored);
        rememberActiveConversationId(id, { surface, storage });
        return messagesToConversation(messages);
      } catch (err) {
        lastError = err;
        if (err?.status === 404 || err?.status === 403) {
          forgetActiveConversationId({ surface, storage });
        }
        return null;
      }
    },

    // Clear starts a genuinely new thread rather than wiping the old one: the
    // point of persisting is that the previous conversation is still there.
    startNew() {
      id = newAgentConversationId();
      nextSeq = 0;
      ensured = null;
      lastError = null;
      queue = Promise.resolve();
      forgetActiveConversationId({ surface, storage });
      return id;
    },

    appendUser({ text = "", attachmentNames = [] } = {}) {
      return enqueue(() =>
        appendWithRetry({
          role: "user",
          content: { text, attachmentNames: attachmentNames.filter(Boolean) },
        })
      );
    },

    appendAssistant({
      text = "",
      status = "complete",
      error = "",
      toolCalls = [],
      confirmations = [],
      mutations = 0,
      tokensIn = 0,
      tokensOut = 0,
    } = {}) {
      return enqueue(() =>
        appendWithRetry({
          role: "assistant",
          content: {
            text,
            status,
            error,
            ...(toolCalls.length > 0 ? { toolCalls } : {}),
            ...(confirmations.length > 0 ? { confirmations } : {}),
          },
          mutations,
          tokensIn,
          tokensOut,
          ...(provider ? { provider } : {}),
          ...(model ? { model } : {}),
        })
      );
    },
  };
}

export default createAgentConversationStore;
